"use client";

import { errorLoggingService } from "@/lib/error-logging-service";
import { db, getClientFunctions, storage } from "@/lib/firebase-client";
import type { FirestoreTimestamp, RecipeBox, RecipeBoxMember, RecipeBoxRole } from "@/lib/types";
import { uploadImage } from "@ezrecipebox/shared";
import {
    collection,
    doc,
    onSnapshot,
    query,
    where,
    type FirestoreError,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

const RECIPE_BOXES_COLLECTION = "recipeBoxes";

let hasLoggedMemberQueryAuthNotReady = false;

type CreateRecipeBoxCallableInput = {
  title: string;
  description?: string;
  imageHint?: string;
};
type CreateRecipeBoxCallableResult = { boxId: string };
type UpdateRecipeBoxCallableInput = { boxId: string; patch: Record<string, unknown> };
type DeleteRecipeBoxCallableInput = { boxId: string };
type GetRecipeBoxCoverUploadPathInput = { boxId: string; fileName: string; contentType?: string };
type GetRecipeBoxCoverUploadPathResult = { storagePath: string };
type FinalizeRecipeBoxCoverInput = { boxId: string; storagePath: string; imageUrl?: string };
type RemoveRecipeBoxCoverInput = { boxId: string };

async function callFunctions<TInput, TOutput>(name: string, input: TInput): Promise<TOutput> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }
  const callable = httpsCallable<TInput, TOutput>(functions, name);
  const response = await callable(input);
  return response.data;
}

export type CreateRecipeBoxInput = {
  title: string;
  description?: string;
  imageHint?: string;
  coverFile?: File | null;
};

export type UpdateRecipeBoxInput = {
  title?: string;
  description?: string;
  imageHint?: string;
  newCoverFile?: File | null;
  currentImageStoragePath?: string;
  removeCoverImage?: boolean;
  labels?: string[];
  imageFocalPointX?: number;
  imageFocalPointY?: number;
  imageZoom?: number;
};

type RecipeBoxMemberFirestore = {
  uid: string;
  role: RecipeBoxRole;
  email?: string;
  displayName?: string;
  photoURL?: string;
  addedAt?: unknown;
};

type RecipeBoxFirestoreDocument = {
  ownerUid: string;
  title: string;
  description: string;
  imageUrl: string;
  imageHint: string;
  imageStoragePath?: string;
  imageFocalPointX?: number;
  imageFocalPointY?: number;
  imageZoom?: number;
  recipeCount: number;
  members?: RecipeBoxMemberFirestore[];
  memberUids?: string[];
  memberRoles?: Record<string, RecipeBoxRole>;
  labels?: string[];
  allowPublicSharing?: boolean;
  createdAt: unknown;
  updatedAt: unknown;
};

/**
 * IMPORTANT:
 * Do NOT create Firestore refs at module scope.
 * Next.js can evaluate client modules during prerender/SSR, and `db` may not be initialized then.
 */
function getRecipeBoxesCollection() {
  return collection(db, RECIPE_BOXES_COLLECTION);
}

const mapMembers = (members?: RecipeBoxMemberFirestore[]): RecipeBoxMember[] =>
  (members ?? []).map((member) => ({
    uid: member.uid,
    role: member.role,
    email: member.email,
    displayName: member.displayName,
    photoURL: member.photoURL,
    addedAt: member.addedAt as FirestoreTimestamp | undefined,
  }));

const normalizeText = (value: string | undefined | null): string => value?.trim() ?? "";

/**
 * Helper to upload a recipe box cover image using the shared upload orchestration.
 * Handles: get path → upload to storage → finalize with server.
 */
async function uploadRecipeBoxCover(boxId: string, file: File): Promise<void> {
  try {
    await uploadImage({
      file,
      getFileName: (f) => f.name,
      getContentType: (f) => f.type,
      getUploadPath: async (fileName, contentType) => {
        const result = await callFunctions<GetRecipeBoxCoverUploadPathInput, GetRecipeBoxCoverUploadPathResult>(
          "getRecipeBoxCoverUploadPath",
          { boxId, fileName, contentType },
        );
        return result.storagePath;
      },
      uploadToStorage: async (f, storagePath, contentType) => {
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, f, { contentType });
        return getDownloadURL(storageRef);
      },
      finalizeUpload: async (storagePath, imageUrl) => {
        await callFunctions<FinalizeRecipeBoxCoverInput, { success?: boolean }>("finalizeRecipeBoxCover", {
          boxId,
          storagePath,
          imageUrl,
        });
      },
    });
  } catch (error) {
    await errorLoggingService.logError({
      context: 'uploadRecipeBoxCover',
      error: error as Error,
      additionalData: {
        boxId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      }
    });
    throw error;
  }
}

export async function createRecipeBox(_ownerUid: string, input: CreateRecipeBoxInput): Promise<string> {
  try {
    // Phase 7: Create via callable (server-owned write). Keep signature stable.
    // ownerUid param kept for backward compatibility; server sets ownerUid from auth.
    const result = await callFunctions<CreateRecipeBoxCallableInput, CreateRecipeBoxCallableResult>("createRecipeBox", {
      title: input.title.trim(),
      description: normalizeText(input.description),
      imageHint: normalizeText(input.imageHint) || "recipe box",
    });

    const boxId = result.boxId;

    if (input.coverFile) {
      await uploadRecipeBoxCover(boxId, input.coverFile);
    }

    return boxId;
  } catch (error) {
    await errorLoggingService.logError({
      context: 'createRecipeBox',
      error: error as Error,
      additionalData: {
        title: input.title,
        hasCoverFile: !!input.coverFile,
      }
    });
    throw error;
  }
}

export async function updateRecipeBox(
  _ownerUid: string,
  recipeBoxId: string,
  input: UpdateRecipeBoxInput,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  try {
    // ownerUid param kept for backward compatibility; server enforces owner permissions.

    if (typeof input.title === "string") patch.title = input.title.trim();
    if (typeof input.description === "string") patch.description = normalizeText(input.description);
    if (typeof input.imageHint === "string") patch.imageHint = normalizeText(input.imageHint) || "recipe box";
    if (input.labels !== undefined) patch.labels = input.labels;
    if (input.imageFocalPointX !== undefined) patch.imageFocalPointX = input.imageFocalPointX;
    if (input.imageFocalPointY !== undefined) patch.imageFocalPointY = input.imageFocalPointY;
    if (input.imageZoom !== undefined) patch.imageZoom = input.imageZoom;

    if (Object.keys(patch).length > 0) {
      await callFunctions<UpdateRecipeBoxCallableInput, { success?: boolean }>("updateRecipeBox", {
        boxId: recipeBoxId,
        patch,
      });
    }

    if (input.removeCoverImage) {
      await callFunctions<RemoveRecipeBoxCoverInput, { success?: boolean }>("removeRecipeBoxCover", { boxId: recipeBoxId });
    } else if (input.newCoverFile) {
      await uploadRecipeBoxCover(recipeBoxId, input.newCoverFile);
    }
  } catch (error) {
    await errorLoggingService.logError({
      context: 'updateRecipeBox',
      error: error as Error,
      additionalData: {
        recipeBoxId,
        hasNewCoverFile: !!input.newCoverFile,
        removeCoverImage: input.removeCoverImage,
        patchFields: Object.keys(patch || {}),
      }
    });
    throw error;
  }
}

export async function deleteRecipeBox(boxId: string): Promise<void> {
  try {
    await callFunctions<DeleteRecipeBoxCallableInput, { success?: boolean }>("deleteRecipeBox", { boxId });
  } catch (error) {
    await errorLoggingService.logError({
      context: 'deleteRecipeBox',
      error: error as Error,
      additionalData: { boxId }
    });
    throw error;
  }
}

export function subscribeToUserRecipeBoxes(
  ownerUid: string,
  onUpdate: (boxes: RecipeBox[]) => void,
  onError?: (error: FirestoreError) => void,
): () => void {
  // Early return if no ownerUid (user not authenticated)
  if (!ownerUid) {
    onUpdate([]);
    return () => {}; // Return no-op cleanup function
  }

  // Query for legacy boxes where user is the owner (covers boxes missing memberUids)
  const ownerQuery = query(getRecipeBoxesCollection(), where("ownerUid", "==", ownerUid));

  // Preferred query: rule-friendly membership lookup
  const memberQuery = query(getRecipeBoxesCollection(), where("memberUids", "array-contains", ownerUid));

  let ownerUnsubscribe: (() => void) | null = null;
  let memberUnsubscribe: (() => void) | null = null;
  
  const ownerBoxes = new Map<string, RecipeBox>();
  const memberBoxes = new Map<string, RecipeBox>();
  
  const updateBoxes = () => {
    // Merge boxes from both queries, avoiding duplicates
    const allBoxes = new Map<string, RecipeBox>();
    
    // Add owner boxes
    ownerBoxes.forEach((box, id) => {
      allBoxes.set(id, box);
    });
    
    // Add member boxes (will overwrite if same ID, but that's fine since owner takes precedence)
    memberBoxes.forEach((box, id) => {
      allBoxes.set(id, box);
    });
    
    onUpdate(Array.from(allBoxes.values()));
  };

  // Subscribe to owner boxes
  ownerUnsubscribe = onSnapshot(
    ownerQuery,
    (snapshot) => {
      ownerBoxes.clear();
      snapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data() as RecipeBoxFirestoreDocument;
        const box: RecipeBox = {
          id: docSnapshot.id,
          ownerUid: data.ownerUid,
          title: data.title,
          description: data.description || "",
          imageUrl: data.imageUrl || "",
          imageHint: data.imageHint || "recipe box",
          imageStoragePath: data.imageStoragePath,
          imageFocalPointX: data.imageFocalPointX,
          imageFocalPointY: data.imageFocalPointY,
          imageZoom: data.imageZoom,
          recipeCount: typeof data.recipeCount === "number" ? data.recipeCount : 0,
          members: mapMembers(data.members),
          memberUids: data.memberUids,
          memberRoles: data.memberRoles,
          labels: data.labels,
          allowPublicSharing: data.allowPublicSharing,
        };
        ownerBoxes.set(docSnapshot.id, box);
      });
      updateBoxes();
    },
    onError,
  );

  // Subscribe to member boxes (includes owner for boxes with memberUids populated)
  let memberRetryTimer: ReturnType<typeof setTimeout> | null = null;
  const subscribeMemberQuery = () => {
    // Clear any prior listener before resubscribing
    if (memberUnsubscribe) {
      memberUnsubscribe();
      memberUnsubscribe = null;
    }

    memberUnsubscribe = onSnapshot(
      memberQuery,
      (snapshot) => {
        memberBoxes.clear();
        snapshot.docs.forEach((docSnapshot) => {
          const data = docSnapshot.data() as RecipeBoxFirestoreDocument;
          const box: RecipeBox = {
            id: docSnapshot.id,
            ownerUid: data.ownerUid,
            title: data.title,
            description: data.description || "",
            imageUrl: data.imageUrl || "",
            imageHint: data.imageHint || "recipe box",
            imageStoragePath: data.imageStoragePath,
            imageFocalPointX: data.imageFocalPointX,
            imageFocalPointY: data.imageFocalPointY,
            imageZoom: data.imageZoom,
            recipeCount: typeof data.recipeCount === "number" ? data.recipeCount : 0,
            members: mapMembers(data.members),
            memberUids: data.memberUids,
            memberRoles: data.memberRoles,
            labels: data.labels,
            allowPublicSharing: data.allowPublicSharing,
          };
          memberBoxes.set(docSnapshot.id, box);
        });
        updateBoxes();
      },
      (error) => {
        // This can happen briefly during auth startup: user exists but token isn't attached yet.
        // If we don't retry, the member stream can stay dead for the session.
        if (error.code === "permission-denied" || error.code === "unauthenticated") {
          if (!hasLoggedMemberQueryAuthNotReady) {
            hasLoggedMemberQueryAuthNotReady = true;
            console.debug(
              "[Recipe Box Service] Member boxes listener blocked (auth not ready yet). Retrying shortly...",
              error.code,
            );
          }

          updateBoxes();

          if (!memberRetryTimer) {
            memberRetryTimer = setTimeout(() => {
              memberRetryTimer = null;
              subscribeMemberQuery();
            }, 750);
          }

          return;
        }

        if (onError) {
          onError(error);
        } else {
          errorLoggingService.logError({
            context: 'subscribeToRecipeBoxes - member query',
            error: error as Error,
          });
        }
      },
    );
  };

  subscribeMemberQuery();

  // Return cleanup function
  return () => {
    if (ownerUnsubscribe) {
      ownerUnsubscribe();
    }
    if (memberUnsubscribe) {
      memberUnsubscribe();
    }
    if (memberRetryTimer) {
      clearTimeout(memberRetryTimer);
      memberRetryTimer = null;
    }
  };
}

export function subscribeToRecipeBox(
  recipeBoxId: string,
  onUpdate: (box: RecipeBox | null) => void,
  onError?: (error: FirestoreError) => void,
): () => void {
  const recipeBoxRef = doc(getRecipeBoxesCollection(), recipeBoxId);

  return onSnapshot(
    recipeBoxRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        onUpdate(null);
        return;
      }

      const data = snapshot.data() as RecipeBoxFirestoreDocument;
      const box: RecipeBox = {
        id: snapshot.id,
        ownerUid: data.ownerUid,
        title: data.title,
        description: data.description || "",
        imageUrl: data.imageUrl || "",
        imageHint: data.imageHint || "recipe box",
        imageStoragePath: data.imageStoragePath,
        imageFocalPointX: data.imageFocalPointX,
        imageFocalPointY: data.imageFocalPointY,
        imageZoom: data.imageZoom,
        recipeCount: typeof data.recipeCount === "number" ? data.recipeCount : 0,
        members: mapMembers(data.members),
      };

      onUpdate(box);
    },
    onError,
  );
}

/**
 * Updates member data in all recipe boxes where the user is a member.
 * This is called when a user updates their profile (displayName, photoURL, etc.)
 * to keep member data in sync across all recipe boxes.
 */
export async function syncUserProfileToRecipeBoxes(
  userId: string,
  profileUpdates: {
    displayName?: string;
    photoURL?: string;
    email?: string;
  },
): Promise<void> {
  if (!userId) {
    return;
  }

  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<
    { displayName?: string; photoURL?: string; email?: string },
    { success: boolean; updatedCount: number }
  >(functions, "syncUserProfileToRecipeBoxes");

  await callable({
    displayName: profileUpdates.displayName,
    photoURL: profileUpdates.photoURL,
    email: profileUpdates.email,
  });
}

